// Tiered price-table products (printing price sheets, CD/DVD packages, ...).
// A category declares option dimensions; each price table applies to one
// combination of option values and lists unit prices at quantity breaks.
//
// Category shape (see config/*.json):
// {
//   id, label, type: "tiered-table",
//   quantityLabel, unitLabel,
//   options: [{ id, label, choices: [{ id, label }] }],
//   tables:  [{ when: { optionId: choiceId, ... }, breaks: [{ qty, unit }], source }],
//   fixedQuantities: true,           // only the listed break quantities can be ordered
//   capAtNextBreak: true,            // a qty between breaks uses the lower break unless the next break totals less
//   aboveMax: "quote" | "last-break",
//   addons:  [{ id, label, kind: "per-unit" | "flat", amount, when?: { optionId: [choiceIds] } }]
// }

import { cents } from "./money.js";

/** Tables whose `when` matches the selections, most specific first. */
function matchTables(category, selections) {
  return category.tables
    .filter((t) => Object.entries(t.when).every(([k, v]) => selections[k] === v))
    .sort((a, b) => Object.keys(b.when).length - Object.keys(a.when).length);
}

/**
 * Choices still available for an option given the other selections, so the
 * UI can hide combinations that have no price table.
 */
export function availableChoices(category, optionId, selections) {
  const option = category.options.find((o) => o.id === optionId);
  return option.choices.filter((c) =>
    category.tables.some((t) =>
      Object.entries(t.when).every(([k, v]) =>
        k === optionId ? v === c.id : selections[k] == null || selections[k] === v,
      ),
    ),
  );
}

export const addonApplies = (addon, selections) =>
  !addon.when || Object.entries(addon.when).every(([k, ids]) => ids.includes(selections[k]));

export function quoteTiered(order, category) {
  const errors = [];
  const warnings = [];
  const selections = order.selections ?? {};
  const qty = Number(order.quantity);

  for (const opt of category.options) {
    if (!selections[opt.id]) errors.push(`Choose ${opt.label.toLowerCase()}.`);
  }
  if (!Number.isInteger(qty) || qty < 1) errors.push(`Enter a ${category.quantityLabel?.toLowerCase() ?? "quantity"}.`);
  if (errors.length) return { category: category.id, status: "incomplete", errors, warnings, total: null };

  const [table] = matchTables(category, selections);
  if (!table) {
    return { category: category.id, status: "quote-requested", errors, warnings: ["That combination isn't on our price sheet yet. We'll quote it by hand."], total: null };
  }

  const breaks = [...table.breaks].sort((a, b) => a.qty - b.qty);
  const min = breaks[0].qty;
  const last = breaks[breaks.length - 1];
  if (qty < min) {
    errors.push(`The minimum for this item is ${min}.`);
    return { category: category.id, status: "incomplete", errors, warnings, total: null, breaks };
  }
  if (qty > last.qty && (category.aboveMax ?? "quote") === "quote") {
    return {
      category: category.id, status: "quote-requested", errors, warnings: [`Orders over ${last.qty} are priced individually. Request a quote and we'll get back to you.`],
      total: null, breaks,
    };
  }

  if (category.fixedQuantities && !breaks.some((b) => b.qty === qty)) {
    errors.push(`Choose one of: ${breaks.map((b) => b.qty.toLocaleString()).join(", ")}.`);
    return { category: category.id, status: "incomplete", errors, warnings, total: null, breaks };
  }

  let idx = breaks.findLastIndex((b) => b.qty <= qty);
  let unit = breaks[idx].unit;
  // Pack-priced breaks (e.g. 250 business cards for $49) carry an exact total.
  let total = breaks[idx].total != null && breaks[idx].qty === qty ? breaks[idx].total : cents(unit * qty);
  let bumped = null;

  // If buying the next break would cost less in total, charge that instead
  // and tell the customer they get the extra pieces.
  const next = breaks[idx + 1];
  if (category.capAtNextBreak && next && cents(next.unit * next.qty) <= total) {
    bumped = next;
    unit = next.unit;
    total = cents(next.unit * next.qty);
    warnings.push(`Ordering ${next.qty} costs the same or less than ${qty}, so we've priced ${next.qty}.`);
  }

  const addons = [];
  for (const id of order.addons ?? []) {
    const a = (category.addons ?? []).find((x) => x.id === id);
    if (!a || !addonApplies(a, selections)) continue;
    const amount = cents(a.kind === "flat" ? a.amount : a.amount * (bumped ? bumped.qty : qty));
    addons.push({ id: a.id, label: a.label, amount });
  }
  const itemsTotal = total;
  total = cents(total + addons.reduce((s, a) => s + a.amount, 0));

  const nextSaving = breaks[(bumped ? breaks.indexOf(bumped) : idx) + 1];
  const upsell = nextSaving ? { qty: nextSaving.qty, unit: nextSaving.unit, total: nextSaving.total ?? cents(nextSaving.unit * nextSaving.qty) } : null;

  return {
    category: category.id,
    status: "priced",
    quantity: bumped ? bumped.qty : qty,
    requestedQuantity: qty,
    unit,
    itemsTotal,
    addons,
    total,
    breaks,
    upsell,
    source: table.source ?? null,
    errors,
    warnings,
  };
}
